import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, unwrap } from "@/api/client";
import { Logo } from "@/components/ui";
import { InstallAppLink } from "@/features/pwa";
import { cn } from "@/lib/cn";
import { SITE_LINK_GROUPS, type SiteLinkGroup } from "@/lib/siteLinks";
import { useAppShell } from "@/lib/useAppShell";
import { SocialIcons } from "./SocialIcons";

/** Help and legal pages added in the CMS (Admin → Content → Pages, "show in footer") join the fixed links. */
export function useFooterGroups(): SiteLinkGroup[] {
  const pages =
    useQuery({
      queryKey: ["pages", "footer"],
      queryFn: () => unwrap(api.GET("/api/v1/pages/")),
      staleTime: 10 * 60_000,
      select: (all) => all.filter((p) => p.show_in_footer),
    }).data ?? [];
  return SITE_LINK_GROUPS.map((group) => {
    if (group.id !== "help") return group;
    const known = new Set(SITE_LINK_GROUPS.flatMap((g) => g.links.map((l) => l.to.split("#")[0])));
    const extra = pages.filter((p) => !known.has(`/${p.slug}`)).map((p) => ({ to: `/${p.slug}`, label: p.title }));
    return { ...group, links: [...group.links, ...extra] };
  });
}

/**
 * Website footer for desktop (full) and tablet (compact, two columns). It isn't rendered at all on
 * phones or in the installed app: those get the tab bar and Account → More instead. CSS hides it for
 * the same cases before scripts run (`[data-site-footer]` in global.css), so it never flashes in.
 */
export function SiteFooter() {
  const { isMobileShell, breakpoint } = useAppShell();
  const groups = useFooterGroups();
  if (isMobileShell) return null;
  const compact = breakpoint === "tablet";
  return (
    <footer data-site-footer className="mt-16 border-t border-line pb-10 pt-10 md:mt-24 md:pt-12">
      <div className="container-rs flex flex-col gap-8">
        <div className={cn("grid gap-8", compact ? "grid-cols-2" : "grid-cols-[1.3fr_repeat(4,minmax(0,1fr))]")}>
          <div className={cn("max-w-sm", compact && "col-span-2")}>
            <Logo />
            <p className="mt-3 text-sm text-ink-muted">
              Where we bring all your dreams to reality. Buy safely with RealitySafe escrow, or chat and deal directly with verified vendors
              across Nigeria.
            </p>
            <SocialIcons className="mt-4" />
          </div>
          {groups.map((group) => (
            <nav key={group.id} aria-label={group.title}>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink-muted">{group.title}</p>
              <ul className="flex flex-col gap-2 text-sm text-ink-soft">
                {group.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="hover:text-ink">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-6">
          <p className="text-xs text-ink-muted">
            © {new Date().getFullYear()} RealityStore · Where we bring all your dreams to reality. Prices in Naira (₦).
          </p>
          <InstallAppLink className="text-sm font-medium text-[var(--primary-text)]" />
        </div>
      </div>
    </footer>
  );
}
